import Option from 'ng/src/app/core/Option';

export abstract class Result<T = unknown> {
  protected constructor() {}
  

  public isValue(): this is ResultValue<T> {
    return Result.isValue(this);
  }

  public isError(): this is ResultError<T> {
    return Result.isError(this);
  }
  
  public spread(): [T | undefined, unknown] {
    if (this.isValue()) {
      return [this.value, undefined];
    } else if (this.isError()) {
      return [undefined, this.error];
    }
    
    return [undefined, undefined];
  }
  
  public toOption(): Option<T> {
    if (this.isValue()) {
      return Option.of(this.value);
    }
    
    return Option.none();
  }
  
  public match<R1, R2>({ value, error }: { value: ValueCallback<T, R1>, error: ErrorCallback<unknown, R2> }): R1 | R2 {
    if (this.isValue()) {
      return value(this.value);
    }
    else if (this.isError()) {
      return error(this.error);
    }
    
    return error(undefined);
  }
  
  toString(): string {
    return `Result(${this.constructor.name})`;
  }
  
  public static tryCatch<T>(fn: () => T): Result<T> {
    try {
      return Result.of(fn());
    } catch (error) {
      return Result.error(error);
    }
  }
  
  public static async asyncTryCatch<T>(fn: () => Promise<T>): Promise<Result<T>> {
    try {
      return Result.of(await fn());
    } catch (error) {
      return Result.error(error);
    }
  }
  
  public static of<T>(value: T): ResultValue<T> {
    return new ResultValue(value);
  }
  
  public static error<T, E>(error: E): ResultError<T, E> {
    return new ResultError(error);
  }

  public static isValue<T>(result: Result<T>): result is ResultValue<T> {
    return result instanceof ResultValue;
  }

  public static isError<T>(result: Result<T>): result is ResultError<T> {
    return result instanceof ResultError;
  }
}

export class ResultValue<T = unknown> extends Result<T> {
  constructor(public readonly value: T) {
    super();
  }
  
  toString(): string {
    return `ResultValue(${this.value})`;
  }
}

export class ResultError<T = unknown, E = unknown> extends Result<T> {
  constructor(public readonly error: E) {
    super();
  }
  
  public match<R1, R2>({ value, error }: { value: ValueCallback<T, R1>, error: ErrorCallback<E, R2> }): R1 | R2 {
    return error(this.error);
  }
  
  toString(): string {
    return `ResultError(${this.error})`;
  }
}

type ValueCallback<T, R> = (val: T) => R;
type ErrorCallback<E, R> = (error: E) => R;

export { Result as default, Result as ResultType };